import type { TextSpan, TextStyle } from "./document";

/**
 * Pure helpers for rich text stored as styled spans. Offsets are UTF-16 code units, the same
 * unit textarea selections and Skia Paragraph use. Every function returns normalised spans:
 * adjacent spans with equal styles are merged and empty spans removed (one empty span is
 * kept when the text is empty, so the node remembers its style).
 */

export function plainText(spans: TextSpan[]): string {
  return spans.map((s) => s.text).join("");
}

const sameStyle = (a: TextStyle, b: TextStyle) => JSON.stringify(a) === JSON.stringify(b);

export function normalizeSpans(spans: TextSpan[]): TextSpan[] {
  const out: TextSpan[] = [];
  for (const s of spans) {
    if (!s.text) continue;
    const last = out[out.length - 1];
    if (last && sameStyle(last.style, s.style)) out[out.length - 1] = { text: last.text + s.text, style: last.style };
    else out.push(s);
  }
  if (!out.length) out.push({ text: "", style: (spans[0] ?? spans[spans.length - 1]).style });
  return out;
}

/** Style that newly typed text at `index` should get (the character before, like editors do). */
export function styleAt(spans: TextSpan[], index: number): TextStyle {
  let pos = 0;
  for (const s of spans) {
    if (index > pos && index <= pos + s.text.length) return s.style;
    pos += s.text.length;
  }
  return spans[0].style;
}

/** Split spans so that `at` falls on a span boundary. */
function splitAt(spans: TextSpan[], at: number): TextSpan[] {
  const out: TextSpan[] = [];
  let pos = 0;
  for (const s of spans) {
    const end = pos + s.text.length;
    if (at > pos && at < end) {
      out.push({ text: s.text.slice(0, at - pos), style: s.style }, { text: s.text.slice(at - pos), style: s.style });
    } else out.push(s);
    pos = end;
  }
  return out;
}

/** Replace [start, end) with `text` (styled like the text before `start` unless given). */
export function replaceText(spans: TextSpan[], start: number, end: number, text: string, style?: TextStyle): TextSpan[] {
  const insertStyle = style ?? styleAt(spans, start);
  const split = splitAt(splitAt(spans, start), end);
  const out: TextSpan[] = [];
  let pos = 0;
  let inserted = false;
  for (const s of split) {
    const sEnd = pos + s.text.length;
    if (!inserted && pos >= start) {
      out.push({ text, style: insertStyle });
      inserted = true;
    }
    if (sEnd <= start || pos >= end) out.push(s);
    pos = sEnd;
  }
  if (!inserted) out.push({ text, style: insertStyle });
  return normalizeSpans(out.length ? out : [{ text, style: insertStyle }]);
}

/** Apply a style patch to [start, end); with start === end, applies to everything. */
export function applyStyle(spans: TextSpan[], start: number, end: number, patch: Partial<TextStyle> | ((s: TextStyle) => Partial<TextStyle>)): TextSpan[] {
  const total = plainText(spans).length;
  if (start === end) [start, end] = [0, total];
  const split = splitAt(splitAt(spans, start), end);
  let pos = 0;
  const out = split.map((s) => {
    const sEnd = pos + s.text.length;
    const inside = pos >= start && sEnd <= end && s.text.length > 0;
    pos = sEnd;
    if (!inside) return s;
    const p = typeof patch === "function" ? patch(s.style) : patch;
    return { text: s.text, style: { ...s.style, ...p } };
  });
  // Empty text: style the placeholder span.
  if (total === 0) return [{ text: "", style: { ...spans[0].style, ...(typeof patch === "function" ? patch(spans[0].style) : patch) } }];
  return normalizeSpans(out);
}

/** Style values shared by every character in [start, end) (or all text if empty range). */
export function commonStyle(spans: TextSpan[], start: number, end: number): Partial<TextStyle> {
  const total = plainText(spans).length;
  if (start === end) {
    // Caret: what typing would produce.
    if (total > 0) return { ...styleAt(spans, start) };
    return { ...spans[0].style };
  }
  let result: Partial<TextStyle> | null = null;
  let pos = 0;
  for (const s of spans) {
    const sEnd = pos + s.text.length;
    if (sEnd > start && pos < end && s.text.length) {
      if (!result) result = { ...s.style };
      else for (const k of Object.keys(result) as (keyof TextStyle)[]) if (JSON.stringify(result[k]) !== JSON.stringify(s.style[k])) delete result[k];
    }
    pos = sEnd;
  }
  return result ?? { ...spans[0].style };
}

/** Uniformly scale font sizes (corner-resizing a text box, like Canva). */
export function scaleFontSizes(spans: TextSpan[], k: number): TextSpan[] {
  return spans.map((s) => ({ text: s.text, style: { ...s.style, fontSize: Math.max(1, Math.round(s.style.fontSize * k * 100) / 100) } }));
}
